import {Box} from 'src/design/atoms/Box';
import fileDownload from 'js-file-download';
import React, {useState} from 'react';
import type {
  GetPersonnelListRequestV2} from 'src/api/ProactApiClient';
import {
  ModuleEnum,
  PermissionOperationEnum,
  UserStatusEnum,
  UserTypeEnum,
} from 'src/api/ProactApiClient';
import {TableRowActions} from 'src/design/molecules/TableRowActions';
import {useTable, useTableState} from 'src/design/organisms/useTable';
import {ContentWrapper} from 'src/app/layout/ContentWrapper';
import {PageHeader} from 'src/design/organisms/PageHeader';
import {TableToolbar} from 'src/design/molecules/TableToolbar';
import {ProjectMultiSelect} from 'src/app/databound-elements/ProjectMultiSelect';
import {Checkbox} from 'src/design/atoms/Checkbox';
import {SidebarLinkPersonnel} from 'src/app/layout/Sidebar/SidebarLinksConfig';
import {useExportPersonnelMutation, useGetPersonnelListV2Query} from 'src/redux/queries/personnel';
import {useHasModulePermission} from 'src/shared/auth/useHasModulePermission';
import {PathUtility} from 'src/shared/constants/PathBuilder';
import {useAlert} from 'src/shared/hooks/useAlert';
import useSetDocumentTitle from 'src/shared/hooks/useSetDocumentTitle';
import {PersonnelImportModal} from '../../../../../../app/personnel/PersonnelImportModal/PersonnelImportModal';
import {PersonnelDocumentTitle} from '../../../../../../app/personnel/personnelHelpers';
import {
  usePersonnelListColumns,
  usePersonnelListRowStyles,
} from './hooks/usePersonnelListColumns';
import {useAppNavigate} from 'src/shared/hooks/useAppNavigate';

export interface PersonnelListProps {
  userType: UserTypeEnum;
  vendorId?: number;
  onRowClick?: (id: number, userType: UserTypeEnum, vendorId?: number) => void;
  disableImport?: boolean;
}
export const PersonnelList: React.FC<PersonnelListProps> = ({
  userType,
  vendorId,
  onRowClick,
  disableImport = false,
}) => {
  useSetDocumentTitle(PersonnelDocumentTitle);

  const {setAlert} = useAlert();
  const navigate = useAppNavigate();

  const columns = usePersonnelListColumns();
  const getRowStyles = usePersonnelListRowStyles();

  const {hasPermission: hasImportPermission} = useHasModulePermission({
    module: ModuleEnum.Personnel,
    operation: PermissionOperationEnum.Import,
  });
  const {hasPermission: hasExportPermission} = useHasModulePermission({
    module: ModuleEnum.Personnel,
    operation: PermissionOperationEnum.Export,
  });
  const {hasPermission: hasCreatePermission} = useHasModulePermission({
    module: ModuleEnum.Personnel,
    operation: PermissionOperationEnum.Create,
  });

  const [showImportPersonnel, setShowImportPersonnel] =
    useState<boolean>(false);
  const [isPendingActionFilterChecked, setIsPendingActionFilterChecked] =
    useState(false);
  const [selectedProjectIds, setSelectedProjectIds] = useState<string[]>([]);

  const handlePendingActionFilter = (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    const {checked} = e.target;
    setIsPendingActionFilterChecked(checked);
  };

  const tableState = useTableState({
    identifier: 'personnel',
    columns: columns,
    defaultColumnFilters: [
      {
        id: 'active',
        value: [UserStatusEnum.Active.toString()],
      },
    ],
    defaultColumnVisibility: {
      jobCode: false,
      'vendor.id': false,
      'vendor.name': userType === UserTypeEnum.Contractor,
    },
    defaultDensity: 'xs',
    defaultPagination: {
      pageIndex: 0,
      pageSize: 20,
    },
    defaultSorting: [{id: 'lastName', desc: false}],
    defaultShowColumnFilters: true,
  });
  const {start, length, filters, filterFns, sorting} = tableState;

  const request: GetPersonnelListRequestV2 = {
    start: start,
    length: length,
    filters: filters,
    filterFns: filterFns,
    sorting: sorting,
    userTypes: [userType],
    vendorId: vendorId,
    hasPendingAction: isPendingActionFilterChecked,
    projectIds:
      selectedProjectIds.length > 0
        ? selectedProjectIds.map(id => parseInt(id, 10))
        : undefined,
  };

  const {data, isLoading, isError, isFetching, refetch} =
    useGetPersonnelListV2Query(request, {refetchOnMountOrArgChange: true});
  const rows = data?.data ?? [];
  const totalCount = data?.totalRecords ?? 0;

  const [exportPersonnelMutation] = useExportPersonnelMutation();

  const exportPersonnel = async () => {
    const exportRequest: GetPersonnelListRequestV2 = {
      ...request,
      length: 1000000,
    };
    const response = await exportPersonnelMutation(exportRequest).unwrap();
    fileDownload(response!.data, `personnel_export.csv`);
    setAlert('Your file has been successfully exported. ', 'success');
  };

  const handleAddClick = () => {
    if (userType === UserTypeEnum.Contractor) {
      navigate(PathUtility.VendorPersonnelCreate(vendorId!));
    } else {
      navigate(PathUtility.PersonnelCreate());
    }
  };

  const {table} = useTable({
    ...tableState,
    tableOptions: {
      mantineTableBodyCellProps: ({row}) => ({
        style: getRowStyles(row.original),
      }),
      manualSorting: true,
      manualFiltering: true,
      manualPagination: true,
      columns: columns,
      data: rows,
      renderRowActions: ({row}) =>
        onRowClick && (
          <TableRowActions>
            <TableRowActions.View
              onClick={() =>
                onRowClick(row.original.id, userType, row.original.vendorId)
              }
            />
          </TableRowActions>
        ),
      renderTopToolbarCustomActions: () => (
        <TableToolbar>
          <TableToolbar.Filters>
            <Checkbox
              label="Has Pending Action"
              checked={isPendingActionFilterChecked}
              onChange={handlePendingActionFilter}
            />
            <Box w={{base: '100%', sm: 220}}>
              <ProjectMultiSelect
                size="sm"
                placeholder="Filter by Project"
                value={selectedProjectIds}
                onChange={setSelectedProjectIds}
              />
            </Box>
          </TableToolbar.Filters>
          <TableToolbar.Actions>
            {hasCreatePermission && (
              <TableToolbar.Action variant="create" onClick={handleAddClick} />
            )}
            {!disableImport && hasImportPermission && (
              <TableToolbar.Action
                variant="import"
                onClick={() => setShowImportPersonnel(true)}
              />
            )}
            {hasExportPermission && (
              <TableToolbar.Action variant="export" onClick={exportPersonnel} />
            )}
          </TableToolbar.Actions>
        </TableToolbar>
      ),
    },
    totalCount,
    isError,
    isLoading,
    isFetching,
    onRefresh: refetch,
  });

  const handleImportSuccess = () => {
    setShowImportPersonnel(false);

    setAlert(
       
      'Your file has been successfully uploaded. You will receive an email after it has been imported with more information.',
      'success'
    );
  };

  return (
    <ContentWrapper>
      <PageHeader>
        <PageHeader.Title icon={SidebarLinkPersonnel.icon}>
          Personnel
        </PageHeader.Title>
      </PageHeader>
      {table}

      <PersonnelImportModal
        show={showImportPersonnel}
        onClose={() => setShowImportPersonnel(false)}
        onImportSuccess={handleImportSuccess}
        userType={userType}
        vendorId={vendorId}
      />
    </ContentWrapper>
  );
};
